import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Heart, CheckCircle2, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * DonateSuccess — landing page Stripe redirects donors to after payment.
 * Polls /donations/status/{session_id} for up to ~15 seconds so the webhook
 * has time to fire and flip the record to `paid`.
 */
export default function DonateSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("pending");
  const [info, setInfo] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        setStatus(data.status || "pending");
        setTries(++attempts);
        if (data.status === "paid") return;
        if (attempts < 8) setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center" data-testid="donate-success-missing">
        <p style={{ color: "var(--ink-soft)" }}>Missing donation session.</p>
        <Link to="/nonprofits" className="btn-primary inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full">
          <ArrowLeft className="h-4 w-4" /> Back to nonprofits
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-16" data-testid="donate-success-page">
      <div className="card-warm rounded-2xl p-10 text-center">
        {status === "paid" ? (
          <>
            <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
                 style={{ backgroundColor: "rgba(132,204,22,0.18)" }}>
              <CheckCircle2 className="h-8 w-8" style={{ color: "var(--avt-lime)" }} />
            </div>
            <h1 className="font-serif text-4xl mt-5" data-testid="donate-success-title">
              Thank you<span style={{ color: "var(--avt-pink)" }}>!</span>
            </h1>
            <p className="mt-3 text-base" style={{ color: "var(--ink-soft)" }}>
              Your <strong>${((info?.amount_cents || 0) / 100).toFixed(2)}</strong> gift to{" "}
              <strong>{info?.org_name || "the nonprofit"}</strong> just landed.
              A tax-deductible receipt is on its way to your inbox.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/nonprofits" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full">
                <Sparkles className="h-4 w-4" /> See more nonprofits
              </Link>
              {info?.event_id && (
                <Link to={`/events/${info.event_id}`}
                      data-testid="donate-success-event-link"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border"
                      style={{ borderColor: "var(--border-warm)", color: "var(--ink)" }}>
                  <Heart className="h-4 w-4" /> View the event
                </Link>
              )}
            </div>
          </>
        ) : status === "error" ? (
          <>
            <p className="text-base" style={{ color: "var(--ink-soft)" }}>
              We couldn&apos;t find that donation. If Stripe charged you, don&apos;t worry &mdash; the receipt
              still comes through. Contact <a href="mailto:hello@addvendtour.com" style={{ color: "var(--avt-purple)" }}>hello@addvendtour.com</a> if not.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
            <h1 className="font-serif text-3xl mt-5">Wrapping up your donation…</h1>
            <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
              Confirming with Stripe. This usually takes a few seconds.
            </p>
            <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              Try {tries}/8
            </p>
          </>
        )}
      </div>
    </div>
  );
}
